"use client";

import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary, alpha,
  Box,
  List,
  ListItem,
  Typography
} from "@mui/material";
import { Link, Locale } from "@/i18n/routing";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NavigationItem, NavItemFields } from "@/types/contentful/common/navigation";
import { SocialLink, Company } from "@/types/contentful/company";
import { File } from "@/types/contentful/footer";
import Image from "next/image";
import { injectSourceIntoUrl } from "@/config/unified-search-config";
import ColletteLogo from "../collette-logo/collette-logo";

interface FooterUIProps {
  children: React.ReactNode;
}

type SocialMediaKey = "Facebook" | "Instagram" | "Twitter" | "LinkedIn" | "YouTube" | "Pinterest" | "CallUs";



// Map social media keys to their respective SVG URLs
const socialIconsMap: Record<SocialMediaKey, string> = {
  Facebook: "https://i.gocollette.com/homepage/footer/fb.svg",
  Instagram: "https://i.gocollette.com/homepage/footer/instagram.svg",
  Twitter: "https://i.gocollette.com/homepage/footer/twitter.svg",
  LinkedIn: "https://i.gocollette.com/homepage/footer/linkedin.svg",
  YouTube: "https://i.gocollette.com/homepage/footer/youtube.svg",
  Pinterest: "https://i.gocollette.com/homepage/footer/pinterest.svg",
  CallUs: "https://i.gocollette.com/homepage/footer/call.svg" // Ensure this URL is correct
};

const FooterUI: React.FC<FooterUIProps> = ({ children }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        padding: "0rem",
        color: "common.white",
        backgroundPosition: "center",
        textAlign: "center"
      }}
      data-testid="footer-ui"
    >
      <Typography
        component="div"
        sx={{
          fontFamily: theme.typography.fontFamily,
          fontSize: "3.75rem",
          fontWeight: 700,
          lineHeight: 1.1,
          color: "common.white",
          textAlign: "center"
        }}
        data-testid="footer-ui-heading"
      >
        {children}
      </Typography>
    </Box>
  );
};

interface HubSpotFormUIProps {
  targetClass: string;
}

const HubSpotFormUI: React.FC<HubSpotFormUIProps> = ({ targetClass }) => (
  <Box
    className={targetClass}
    sx={{
      minHeight: "150px",
      padding: "0.5rem",
      backgroundColor: (theme) => alpha(theme.palette.common.white, 0.2),
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}
    data-testid="hubspot-form-ui"
  />
);

export { FooterUI, HubSpotFormUI };

interface HPRFooterUIProps {
  menus: NavigationItem[];
  socials: SocialLink[];
  company: Company[];
  backgroundImage: File;
  locale: Locale;
}

export const SitemapContainerUI: React.FC<HPRFooterUIProps> = ({
  menus,
  socials,
  company,
  backgroundImage,
  locale
}) => {
  const theme = useTheme();
  const [expandedAccordion, setExpandedAccordion] = React.useState<string | false>(false);
  const phoneNumber = company[0]?.phoneNumber;

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordion(isExpanded ? panel : false);
    };

  // Helper function to generate test IDs with sequential numbers
  const generateTestId = (parentType: string, childType: string, id: string) =>
    `${parentType}-${childType}${id}`;

  return (
    <footer data-testid="smc-footer-ui">
      <Box
        sx={{
          backgroundImage: `url(https:${backgroundImage.url})`,
          backgroundSize: "100% auto",
          paddingTop: "0rem"
        }}
        data-testid="smc-footer-container"
      >
        {/* Desktop Grid View */}
        <Box
          sx={{
            display: {
              xs: "none", [theme.breakpoints.up(992)]: {
                display: "grid"
              }
            },
            paddingLeft: "0rem",
            gridTemplateColumns: { md: "1.5fr repeat(4, 1fr)" },
            gap: "2rem 4rem", // Add vertical and horizontal spacing
            textAlign: "left",
            alignItems: "stretch"
          }}
          data-testid="desktop-grid-view"
        >
          {/* First Column: Social Links + Our Tours */}
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }} data-testid="first-column">
            {/* Social Links */}
            <Box
              sx={{
                display: "flex",
                gap: "2.3rem",
                justifyContent: "flex-end",
                backgroundColor: "background.brand",
                height: "8%",
                padding: "10px 10px 10px 10px",
                overflowX: "auto",
                whiteSpace: "nowrap",
                "&::-webkit-scrollbar": {
                  display: "none"
                }
              }}
              data-testid="social-links-container"
            >
              {socials.map((social, index) => {
                const title = social?.fields.title as SocialMediaKey;
                const iconUrl = socialIconsMap[title]; // Get the SVG URL
                const href = title === "CallUs" ? `tel:${phoneNumber}` : social?.fields.url;

                return (
                  <Link
                    key={social?.fields.title}
                    prefetch={false}
                    href={href}
                    target={title === "CallUs" ? "_self" : "_blank"}
                    rel="noopener"
                    aria-label={social?.fields.title}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "1.25rem",
                      color: "var(--theme-palette-common-white)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = "none";
                    }}
                    data-testid={generateTestId("social-link", "icon", index.toString())}
                  >
                    {/* Use <img> to load the SVG icon */}
                    <Image
                      src={iconUrl}
                      alt={social?.fields.title}
                      width={15}
                      height={15}
                      style={{ width: "15px", height: "15px", borderWidth: "5px" }} // Adjust size
                    />
                  </Link>
                );
              })}
            </Box>

            {/* Our Tours */}
            <Box
              sx={{
                backgroundColor: "accent.skyLight",
                padding: "2rem 5rem",
                borderRadius: "0px",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
                alignItems: "center"
              }}
              data-testid="our-tours-container"
            >
              {/* "Our Tours" Heading */}
              <Link
                href={menus[0].url ? injectSourceIntoUrl(menus[0].url, "footer_nav_item_url") : ""}
                prefetch={false}
                style={{ textDecoration: "none", width: "100%" }}
                data-testid="our-tours-link" 
              >
                <Typography
                  variant="subtitle_lg"
                  component="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "text.primary",
                    textAlign: "left",
                    width: "100%",
                    paddingLeft: "1rem"
                  }}
                  data-testid="our-tours-heading"
                >
                  Our Tours
                </Typography>
              </Link>

              {/* Tours List */}
              <List
                sx={{
                  padding: 0,
                  listStyle: "none",
                  flexGrow: 1,
                  margin: 0,
                  width: "100%",
                  textAlign: "left"
                }}
                data-testid="our-tours-list"
              >
                {menus[0].items.map((item, index) => {
                  const ourToursItemUrl = injectSourceIntoUrl(item.url, "footer_nav_item_url");
                  return (
                    <ListItem
                      key={item.url}
                      sx={{
                        padding: "0.01rem 1rem",
                        margin: "2.25rem 0",
                        lineHeight: 1.0
                      }}
                      data-testid={generateTestId("our-tours-item", "link", index.toString())}
                    >
                      <Link
                        href={ourToursItemUrl}
                        prefetch={false}
                        style={{
                          color: "var(--theme-palette-text-primary)",
                          textDecoration: "none",
                          fontSize: "1.3rem",
                          fontFamily: theme.typography.fontFamily,
                          fontWeight: 500
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = "none";
                        }}
                        data-testid={generateTestId("our-tours-item", "link", index.toString())}
                      >
                        {item.title}
                      </Link>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>

          {/* Other Columns */}
          {menus.slice(1).map((menu, index) => (
            <Box
              key={menu.title}
              sx={{
                height: "100%",
                position: "relative",
                paddingTop: "1rem",
                paddingBottom: "2rem",
                "&:nth-of-type(n + 3):before": {
                  content: "\"\"",
                  position: "absolute",
                  left: "-3rem",
                  top: 0,
                  bottom: 0,
                  width: "1px",
                  backgroundColor: alpha(theme.palette.common.white, 0.3)
                }
              }}
              data-testid={generateTestId("menu-column", "container", index.toString())}
            >
              {/* Only show heading if there are visible sub-items */}
              {menu.items && menu.items.some(item => {
                const navItemFields = item as unknown as NavItemFields;
                return !navItemFields.hideOnCobrand;
              }) && (
                  <Link
                    href={menu.url}
                    prefetch={false}
                    style={{ textDecoration: "none" }}
                    data-testid={generateTestId("menu-column", "heading-link", index.toString())}
                  >
                    <Typography
                      variant="subtitle_lg"
                      component="h6"
                      sx={{ marginBottom: "1rem", fontWeight: "bold", color: "common.white" }}
                      data-testid={generateTestId("menu-column", "heading", index.toString())}
                    >
                      {menu.title}
                    </Typography>
                  </Link>
                )}

              <List
                sx={{ padding: 0, listStyle: "none", margin: 0 }}
                data-testid={generateTestId("menu-column", "list", index.toString())}
              >
                {menu.items && menu.items.map((item, itemIndex) => {
                  const navItemFields = item as unknown as NavItemFields;
                  if (!navItemFields.hideOnCobrand) {
                    return (
                      <ListItem
                        key={item.url}
                        sx={{
                          padding: 0,
                          margin: "2.25rem 0",
                          textAlign: "left"
                        }}
                        data-testid={generateTestId("menu-column-item", "link", itemIndex.toString())}
                      >
                        <Link
                          href={item.url}
                          prefetch={false}
                          style={{
                            color: "var(--theme-palette-common-white)",
                            textDecoration: "none",
                            fontSize: "1.3rem",
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: 500,
                            lineHeight: 1.2,
                            display: "inline-block",
                            width: "100%"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = "underline";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = "none";
                          }}
                          data-testid={generateTestId("menu-column-item", "link", itemIndex.toString())}
                        >
                          {item.title}
                        </Link>
                      </ListItem>
                    );
                  }
                  return null;
                })}
              </List>

              {/* Show logo in last column regardless of items */}
              {index === menus.length - 2 && (
                <Box sx={{ marginTop: "2rem" }} data-testid="collette-logo-container">
                  <ColletteLogo
                    company={company[0]}
                    logoType="footerLogo"
                    fallbackurl="/images/collette-header-logo.png"
                    data-testid="collette-logo"
                    height={100}
                    width={100}
                    locale={locale}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* Mobile Accordion View */}
        <Box sx={{
          display: "block",
          [theme.breakpoints.up(992)]: {
            display: "none"
          }
        }} data-testid="mobile-accordion-view">
          {/* Social Links for Mobile */}
          <Box
            sx={{
              display: "flex",
              gap: "3rem",
              height: "40px",
              flexWrap: "wrap",
              justifyContent: "center",
              backgroundColor: "background.brand"
            }}
            data-testid="mobile-social-links-container"
          >
            {socials.map((social, index) => {
              const title = social?.fields.title as SocialMediaKey;
              const iconUrl = socialIconsMap[title]; // Get the SVG URL

              return (
                <Link
                  key={social?.fields.title}
                  href={social?.fields.url}
                  prefetch={false}
                  target="_blank"
                  rel="noopener"
                  aria-label={social?.fields.title}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "1.25rem",
                    color: "var(--theme-palette-common-white)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = "none";
                  }}
                  data-testid={generateTestId("mobile-social-link", "icon", index.toString())}
                >
                  {/* Use <img> to load the SVG icon */}
                  <Image
                    src={iconUrl}
                    alt={social?.fields.title}
                    width={15}
                    height={15}
                    style={{ width: "15px", height: "15px", borderWidth: "5px" }} // Adjust size
                  />
                </Link>
              );
            })}
          </Box>

          {/* Accordions for Mobile */}
          {menus.map((menu, index) => {
            // Check if there are any visible items in this menu
            const hasVisibleItems = menu.items && menu.items.some(item => {
              const navItemFields = item as unknown as NavItemFields;
              return !navItemFields.hideOnCobrand;
            });

            // Only render the accordion if there are visible items
            if (!hasVisibleItems) return null;

            return (
              <Accordion
                slotProps={{ heading: { component: "div" } }} // Override the implicit H3 heading wrapper's component
                key={menu.title}
                expanded={expandedAccordion === menu.title}
                onChange={handleAccordionChange(menu.title)}
                sx={{
                  backgroundColor: "background.brand",
                  borderColor: "common.white",
                  borderWidth: "10px",
                  color: "common.white",
                  boxShadow: 0,
                  margin: 0,
                  "&.Mui-expanded": {
                    margin: 0
                  },
                  "&:before": {
                    display: "none"
                  }
                }}
                data-testid={generateTestId("mobile-accordion", "container", index.toString())}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "common.white" }} />}
                  sx={{ justifyContent: "flex-start", textAlign: "left" }}
                  data-testid={generateTestId("mobile-accordion", "summary", index.toString())}
                >
                  <Typography
                    component="div" // Explicitly render as a div
                    sx={{ fontWeight: "bold", color: "common.white", fontSize: "24px", fontFamily: "inherit" }} // Keep styles, ensure font inherits
                    data-testid={generateTestId("mobile-accordion", "heading", index.toString())}
                  >
                    {menu.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: 0,
                    margin: 0,
                    textAlign: "left",
                    backgroundColor: expandedAccordion === menu.title ? "background.default" : "transparent"
                  }}
                  data-testid={generateTestId("mobile-accordion", "details", index.toString())}
                >
                  <List
                    sx={{ padding: "40px 0 40px 16px", listStyle: "none", margin: 0 }}
                    data-testid={generateTestId("mobile-accordion", "list", index.toString())}
                  >
                    {menu.items.map((item, itemIndex) => {
                      const navItemFields = item as unknown as NavItemFields;
                      if (navItemFields.hideOnCobrand) return null;
                      const navItemUrl = injectSourceIntoUrl(item.url, "mofooter_nav_item_url");
                      return (
                        <ListItem
                          key={item.url}
                          sx={{ padding: 0, margin: "2.25rem 0" }}
                          data-testid={generateTestId("mobile-accordion-item", "link", itemIndex.toString())}
                        >
                          <Link
                            href={navItemUrl}
                            prefetch={false}
                            style={{
                              color: expandedAccordion === menu.title ? "var(--theme-palette-text-primary)" : "var(--theme-palette-common-white)",
                              textDecoration: "none",
                              fontSize: "13px",
                              fontFamily: theme.typography.fontFamily,
                              fontWeight: 500,
                              textAlign: "left"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration = "underline";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = "none";
                            }}
                            data-testid={generateTestId("mobile-accordion-item", "link", itemIndex.toString())}
                          >
                            {item.title}
                          </Link>
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Box>
    </footer>
  );
};